import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader } from 'lucide-react';
import Button from './Button';
import Icon from '../AppIcon';
import { bibleService } from '../../services/bibleService';

const SearchBar = ({ 
  onSearch, 
  onVerseSelect, 
  placeholder = "Search...",
  className = "" 
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const debounceRef = useRef(null);

  // Handle search with debouncing
  useEffect(() => {
    if (query?.trim()?.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Clear previous debounce
    if (debounceRef?.current) {
      clearTimeout(debounceRef?.current);
    }

    // Set new debounce
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await bibleService?.searchVerses(query?.trim(), 10);
        
        if (error) {
          setError('Search failed. Please try again.');
          setResults([]);
        } else {
          setResults(data || []);
          setIsOpen(true);
        }
      } catch (err) {
        setError('Search failed. Please try again.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef?.current) {
        clearTimeout(debounceRef?.current);
      }
    };
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setQuery(value);
    setSelectedIndex(-1);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (query?.trim()) {
      onSearch?.(query?.trim());
      setIsOpen(false);
    }
  };

  const handleVerseClick = (verse) => {
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onVerseSelect?.(verse);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    searchRef?.current?.querySelector('input')?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results?.length === 0) {
      if (e?.key === 'Enter') {
        handleSubmit(e);
      }
      return;
    }

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < results?.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : results?.length - 1
        );
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results?.length) {
          handleVerseClick(results?.[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Truncate text helper
  const truncateText = (text, maxLength = 100) => {
    if (text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full pl-10 pr-10 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </form>

      {/* Search Results Dropdown */}
      {isOpen && (query?.length >= 3) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          {loading && (
            <div className="p-4 text-center">
              <Loader className="h-4 w-4 animate-spin mx-auto mb-2" />
              <span className="text-sm text-muted-foreground">Searching...</span>
            </div>
          )}

          {error && (
            <div className="p-4 text-center">
              <Icon name="AlertCircle" size={16} className="text-destructive mx-auto mb-2" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {!loading && !error && results?.length === 0 && (
            <div className="p-4 text-center">
              <Icon name="Search" size={16} className="text-muted-foreground mx-auto mb-2" />
              <span className="text-sm text-muted-foreground">No verses found</span>
            </div>
          )}

          {!loading && !error && results?.length > 0 && (
            <div className="py-2">
              <div className="px-3 py-1 text-xs font-medium text-muted-foreground border-b border-border">
                {results?.length} verse{results?.length !== 1 ? 's' : ''} found
              </div>
              {results?.map((verse, index) => (
                <button
                  key={verse?.verse_id || index}
                  onClick={() => handleVerseClick(verse)}
                  className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors ${
                    selectedIndex === index ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-primary mb-1">
                        {verse?.reference || `${verse?.book_name} ${verse?.chapter_number}:${verse?.verse_number}`}
                      </div>
                      <div className="text-sm text-foreground leading-relaxed">
                        {truncateText(verse?.text)}
                      </div>
                      {verse?.rank && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Relevance: {Math?.round(verse?.rank * 100)}%
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;